import { useMutation } from "@tanstack/react-query";
import { useDependencies } from "./useDependencies";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./useAuth";
import { DOCUMENTATION_AREA_ROUTES } from "../routes/Navigation";
import { useSlug } from "./useSlug";
import { useGetUserLink } from "./useGetUserLink";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { useGetCustomerCustomization } from "./useGetCustomization";
import { useNavigateToErrorScreen } from "./useNavigateToErrorScreen";
import { LoginTypes } from "../../contexts/auth/application/AuthController";

export const useSendTwoFactor = () => {
  const navigate = useNavigate();
  const navigateToErrorScreen = useNavigateToErrorScreen();
  const { authController } = useDependencies();
  const { setEmailSubmitError } = useAuth();
  const { linkType, slug } = useSlug();
  const { userLink } = useGetUserLink();
  const { data: customization } = useGetCustomerCustomization();

  const mutationFn = async (email: string) => {
    if (!linkType) {
      throw new Error("Link type can't be undefined.");
    }

    const loginType = customization?.documentationAreaLoginMethod ?? LoginTypes.EMAIL_CODE;

    await authController.sendTwoFactor({
      email,
      linkType,
      linkId: userLink?.id ?? null,
      slug,
      loginType,
    });
    return email;
  };

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess: () => {
      navigate({ pathname: DOCUMENTATION_AREA_ROUTES.TWO_FACTOR, search: location.search });
    },
    onError: (error) => {
      if (!(error instanceof HTTPError)) {
        return;
      }

      if (error.getHTTPCode().isBadRequest()) {
        setEmailSubmitError("documentation_area.login.email.error");
        return;
      }

      if (error.getHTTPCode().isForbidden()) {
        navigate({ pathname: DOCUMENTATION_AREA_ROUTES.LOGIN, search: location.search });
        return;
      }

      navigateToErrorScreen(error);
    },
  });

  return {
    mutate,
    isPending,
  };
};
